import React, { ReactNode } from 'react'
import { Drawer as DrawerPrimitive } from 'vaul'
import { clsx } from '@unbridaled/shared/utils/clsx'
import { IconProps } from '@unbridaled/icons'
import { RenderComponent } from 'types/react'
import { render } from 'utils/render'
import { CloseLargeIcon } from '@unbridaled/icons/CloseLargeIcon'

export const DrawerTitle = (
  props: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
): React.ReactNode => (
  <DrawerPrimitive.Title
    data-slot="title"
    className="uui-text-lg uui-font-medium uui-truncate"
    {...props}
  />
)

export const DrawerOverlay = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    data-slot="overlay"
    className={clsx('uui-fixed uui-inset-0 uui-bg-black/30 uui-z-overlay', className)}
    {...props}
  />
))
export const DrawerDescription = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    data-slot="description"
    className={clsx('uui-text-tertiary uui-text-sm', className)}
    {...props}
  />
))

export const DrawerHeader = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Title>,
  Omit<React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>, 'title'> & {
    children?: React.ReactNode
    className?: string
    title?: React.ReactNode
    leadingIcon?: RenderComponent<IconProps>
    trailingIcon?: RenderComponent<IconProps>
    description?: React.ReactNode
    direction?: 'left' | 'right' | 'center'
    hidden?: boolean
    hideCloseButton?: boolean
    hiddenDescription?: boolean
    /**
     * @deprecated use trailingIcon or hideCloseButton to hide
     */
    onClose?: () => void
  }
>(
  (
    {
      className,
      title,
      children,
      leadingIcon,
      trailingIcon,
      description,
      onClose,
      direction,
      hidden,
      hideCloseButton,
      hiddenDescription,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      data-slot="header"
      className={clsx(
        'uui-sticky uui-top-0 uui-z-10 uui-flex uui-justify-between uui-items-center uui-space-x-2 uui-overflow-hidden',
        'uui-bg-white uui-text-primary uui-py-3 uui-px-6 uui-border-b uui-border-primary',
        hidden ? 'uui-hidden' : '',
        className
      )}
      {...props}
    >
      <div
        className={clsx('uui-flex uui-items-center', direction === 'center' ? 'uui-flex-1' : '')}
      >
        {
          render(leadingIcon, {
            color: 'uui-text-current',
            size: 20
          }) as ReactNode
        }
      </div>
      <div className="uui-text-center uui-truncate">
        <DrawerTitle className="uui-text-lg uui-font-medium uui-truncate">
          {title || children}
        </DrawerTitle>
        <DrawerDescription
          className={clsx(
            'uui-truncate',
            hiddenDescription ? 'uui-hidden' : description ? '' : 'uui-hidden'
          )}
        >
          {description || title}
        </DrawerDescription>
      </div>
      <div className="uui-flex uui-justify-end uui-items-center uui-flex-1">
        {!trailingIcon && !hideCloseButton && (
          <DrawerPrimitive.Close className="uui-ml-auto">
            <CloseLargeIcon size={20} color="uui-text-current" />
          </DrawerPrimitive.Close>
        )}
        {
          render(trailingIcon, {
            color: 'uui-text-current',
            size: 20
          }) as ReactNode
        }
      </div>
    </div>
  )
)

export const DrawerContent = ({
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>): React.ReactNode => (
  <div data-slot="content" className={clsx('uui-p-4', className)} {...props}>
    {children}
  </div>
)

export interface DrawerFooterProps extends React.ComponentPropsWithoutRef<'div'> {
  behavior?: 'sticky' | 'block'
}
export const DrawerFooter = ({
  children,
  behavior = 'sticky',
  className,
  ...props
}: DrawerFooterProps): React.ReactNode => (
  <div
    data-slot="footer"
    className={clsx(
      'uui-flex uui-items-center uui-justify-end uui-overflow-hidden',
      'uui-py-3 uui-px-4 uui-space-x-2.5',
      'uui-border-t uui-border-primary uui-bg-white',
      behavior === 'sticky' ? 'uui-sticky uui-bottom-0 uui-left-0 uui-right-0' : '',
      className
    )}
    {...props}
  >
    {children}
  </div>
)
